'use client'

import React, { useState, useRef, useEffect } from 'react'
import { BodyPortal } from './BodyPortal'

export interface ActionMenuItem {
  icon: string
  label: string
  onClick: () => void
  show?: boolean
  danger?: boolean
}

interface TableActionMenuProps {
  items: ActionMenuItem[]
  rowId: number | string
  openMenuRowId?: number | string | null
  onOpenChange?: (rowId: number | string | null) => void
}

export function TableActionMenu({ items, rowId, openMenuRowId, onOpenChange }: TableActionMenuProps) {
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const isOpen = openMenuRowId === rowId

  const visibleItems = items.filter(item => item.show !== false)

  // Dışarıya tıklayınca kapat
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      if (
        wrapperRef.current && !wrapperRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        onOpenChange?.(null)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onOpenChange])

  // Açıldığında trigger ikonunun ekran koordinatlarına göre pozisyon hesapla
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const triggerCenterY = rect.top + rect.height / 2
      const dropdownHeight = (visibleItems.length * 36) + 16 // tahmini yükseklik
      const arrowOffset = 16 // ok işaretinin dropdown içindeki merkez noktası
      const spaceBelow = window.innerHeight - triggerCenterY
      const spaceAbove = triggerCenterY
      const openTop = spaceBelow < dropdownHeight + 10 && spaceAbove > dropdownHeight + 10

      setPosition(openTop ? 'top' : 'bottom')

      // Dropdown sağ kenarı = trigger sol kenarı - 5px boşluk
      const rightOffset = window.innerWidth - rect.left + 5

      if (openTop) {
        // Ok işareti ikon hizasında: dropdown alt kenarından arrowOffset yukarıda
        setDropdownStyle({
          position: 'fixed',
          bottom: window.innerHeight - triggerCenterY - arrowOffset,
          right: rightOffset,
          zIndex: 99999,
        })
      } else {
        // Ok işareti ikon hizasında: dropdown üst kenarından arrowOffset aşağıda
        setDropdownStyle({
          position: 'fixed',
          top: triggerCenterY - arrowOffset,
          right: rightOffset,
          zIndex: 99999,
        })
      }
    }
  }, [isOpen, visibleItems.length])

  // Scroll veya resize olunca kapat
  useEffect(() => {
    if (!isOpen) return
    const close = () => onOpenChange?.(null)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [isOpen, onOpenChange])

  const handleMouseEnter = () => {
    onOpenChange?.(rowId)
  }

  return (
    <div
      ref={wrapperRef}
      className={`table-action-wrapper ${isOpen ? 'is-open' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => {}}
    >
      <div ref={triggerRef} className="table-action-trigger">
        <i className="fa-solid fa-ellipsis-vertical" />
      </div>

      {isOpen && (
        <BodyPortal>
          <div
            ref={dropdownRef}
            className={`table-action-dropdown position-${position}`}
            style={dropdownStyle}
          >
            <div className="table-action-dropdown-arrow" />
            {visibleItems.map((item, index) => (
              <button
                key={index}
                className={`table-action-item${item.danger ? ' is-danger' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenChange?.(null)
                  item.onClick()
                }}
              >
                <i className={item.icon} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </BodyPortal>
      )}
    </div>
  )
}
